/**
 * Helpers to build structured-data (JSON-LD) objects for the Seo component.
 * Keep the shape close to schema.org so rich results in Google pick up.
 */

const SITE_URL = "https://wishuponaplayhouse.com";
const BUSINESS_ID = `${SITE_URL}/#business`;

/** Extract a plain decimal string from a user-entered price like "$5,400.00". */
export function parsePrice(priceStr) {
  if (priceStr == null) return null;
  const cleaned = String(priceStr).replace(/[^0-9.]/g, "").trim();
  if (!cleaned) return null;
  const num = Number(cleaned);
  return Number.isFinite(num) && num > 0 ? num.toFixed(2) : null;
}

/**
 * Product JSON-LD for an individual playhouse page.
 */
export function playhouseProductJsonLd({
  name,
  description,
  imagePath,
  price,
  slug,
}) {
  if (!name) return null;
  const priceDecimal = parsePrice(price);
  const node = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description: description || `${name} — a custom children's playhouse handcrafted on-site in Denver, Colorado by Wish Upon A Playhouse.`,
    image: imagePath ? `${SITE_URL}${imagePath}` : undefined,
    url: slug ? `${SITE_URL}${slug}` : undefined,
    brand: { "@type": "Brand", "name": "Wish Upon A Playhouse" },
    category: "Children's Playhouse",
    itemCondition: "https://schema.org/NewCondition",
  };
  if (priceDecimal) {
    node.offers = {
      "@type": "Offer",
      priceCurrency: "USD",
      price: priceDecimal,
      availability: "https://schema.org/InStock",
      url: slug ? `${SITE_URL}${slug}` : SITE_URL,
      seller: { "@id": BUSINESS_ID },
      areaServed: { "@type": "State", "name": "Colorado" },
    };
  }
  // Drop undefined keys for a cleaner payload
  Object.keys(node).forEach((k) => node[k] === undefined && delete node[k]);
  return node;
}

/**
 * FAQPage JSON-LD from an array of { question, answer } items.
 */
export function faqPageJsonLd(items) {
  if (!Array.isArray(items) || items.length === 0) return null;
  const mainEntity = items
    .filter((it) => it && (it.question || it.q) && (it.answer || it.a))
    .map((it) => ({
      "@type": "Question",
      name: it.question || it.q,
      acceptedAnswer: { "@type": "Answer", text: it.answer || it.a },
    }));
  if (mainEntity.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity,
  };
}

/**
 * LocalBusiness + aggregateRating + review list JSON-LD for the reviews page.
 * Each review item: { name, rating (1-5), comment, location?, createdAt? }
 */
export function reviewsBusinessJsonLd(reviews) {
  if (!Array.isArray(reviews) || reviews.length === 0) return null;
  const valid = reviews.filter((r) => r && typeof r.rating === "number" && r.comment);
  if (valid.length === 0) return null;

  const total = valid.reduce((sum, r) => sum + r.rating, 0);
  const avg = total / valid.length;

  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": BUSINESS_ID,
    name: "Wish Upon A Playhouse",
    url: SITE_URL,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: avg.toFixed(1),
      reviewCount: valid.length,
      bestRating: 5,
      worstRating: 1,
    },
    review: valid.slice(0, 20).map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", "name": r.name || "Anonymous" },
      reviewRating: {
        "@type": "Rating",
        ratingValue: r.rating,
        bestRating: 5,
        worstRating: 1,
      },
      reviewBody: r.comment,
      datePublished: r.createdAt || undefined,
    })),
  };
}

/**
 * BreadcrumbList JSON-LD. Pass an ordered array of { name, path }.
 * Home is implicit unless included explicitly.
 */
export function breadcrumbJsonLd(items) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}
